import React, { useState } from "react";
import { Form, Upload, Button, message, Avatar } from "antd";
import {
  UploadOutlined,
  UserOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useStaffData } from "../../hooks/useStaffData";
import {
  createEmployeeFormData,
  transformFormDataToApi,
} from "../../utils/dataUtils";
import { uploadToCloudinary } from "@/utils/cloudinaryUpload"; // same util as AddProductModal
import {
  NameFieldsRow,
  EmailField,
  DateField,
  TextField,
  // AvatarUploadField  ← replaced with inline Dragger below
} from "@/components/UI/FormFields";
import {
  PhoneGenderRow,
  TeamRoleRow,
  LocationField,
} from "../../components/StaffFormFields";

const { Dragger } = Upload;

const EmployeeSingleUpload = ({
  onCancel,
  onSubmit,
  loading = false,
  formRef,
  mode = "create",
  employeeData = null,
  onLoadingChange,
  onFormValidChange,
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Avatar state (mirrors AddProductModal pattern) ──────────────────────
  const [avatarFileList, setAvatarFileList] = useState([]);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null); // local blob for instant preview
  const [avatarCloudinaryUrl, setAvatarCloudinaryUrl] = useState(null); // final Cloudinary URL
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Use shared data hook
  const {
    rawTeamsData,
    rawRolesData,
    rawLocationsData,
    isLoadingTeams,
    isLoadingRoles,
    isLoadingLocations,
  } = useStaffData();

  // Notify parent of loading state
  React.useEffect(() => {
    onLoadingChange?.(isSubmitting || isUploadingAvatar);
  }, [isSubmitting, isUploadingAvatar, onLoadingChange]);

  // ── Form validity check ─────────────────────────────────────────────────
  const checkFormValidity = React.useCallback(() => {
    try {
      const values = form.getFieldsValue();

      if (mode === "create") {
        const basicFieldsValid =
          values.firstName?.trim() &&
          values.lastName?.trim() &&
          values.email?.trim() &&
          values.phoneNumber?.trim() &&
          values.gender &&
          values.address?.trim();

        const teamValid = Array.isArray(values.team) && values.team.length > 0;
        const locationValid =
          Array.isArray(values.locationId) && values.locationId.length > 0;
        const hasErrors = form
          .getFieldsError()
          .some(({ errors }) => errors.length > 0);
        const isValid = !!(
          basicFieldsValid &&
          teamValid &&
          locationValid &&
          !hasErrors
        );

        onFormValidChange?.(isValid);
        return isValid;
      }

      if (mode === "edit") {
        const hasErrors = form
          .getFieldsError()
          .some(({ errors }) => errors.length > 0);
        const isValid = !hasErrors;
        onFormValidChange?.(isValid);
        return isValid;
      }

      return false;
    } catch (error) {
      console.error("Error checking form validity:", error);
      onFormValidChange?.(false);
      return false;
    }
  }, [form, mode, onFormValidChange]);

  // ── Expose form to parent via ref ───────────────────────────────────────
  React.useImperativeHandle(
    formRef,
    () => ({
      submit: () => {
        form.validateFields().then(
          () => form.submit(),
          (errorInfo) => {
            console.error("Validation failed during manual submit:", errorInfo);
            message.error(
              "Please correct the errors in the form before submitting.",
            );
          },
        );
      },
      isLoading: isSubmitting,
      isFormValid: checkFormValidity(),
    }),
    [isSubmitting, form, checkFormValidity],
  );

  // ── Populate form in edit / view mode ───────────────────────────────────
  React.useEffect(() => {
    if ((mode === "edit" || mode === "view") && employeeData) {
      const formData = createEmployeeFormData(employeeData);

      if (formData.dateOfBirth) {
        formData.dateOfBirth = moment(formData.dateOfBirth);
      }

      // Pre-populate avatar preview from existing data
      if (employeeData.avatar) {
        setAvatarCloudinaryUrl(employeeData.avatar);
        setAvatarPreviewUrl(employeeData.avatar);
        // Show existing image as a "done" file in the dragger
        setAvatarFileList([
          {
            uid: "-1",
            name: "avatar.png",
            status: "done",
            url: employeeData.avatar,
          },
        ]);
      }

      form.setFieldsValue(formData);
      setTimeout(() => checkFormValidity(), 150);
    }
  }, [mode, employeeData, form, checkFormValidity]);

  // Reset on create mode
  React.useEffect(() => {
    if (!mode || mode === "create") {
      setIsSubmitting(false);
      setAvatarFileList([]);
      setAvatarPreviewUrl(null);
      setAvatarCloudinaryUrl(null);
      onFormValidChange?.(false);
    }
  }, [mode, onFormValidChange]);

  // ── Avatar upload — same pattern as AddProductModal ─────────────────────
  const handleAvatarUpload = async (file) => {
    try {
      setIsUploadingAvatar(true);
      message.loading({ content: "Uploading avatar...", key: "avatar-upload" });

      const url = await uploadToCloudinary(file);

      setAvatarCloudinaryUrl(url);
      message.success({ content: "Avatar uploaded!", key: "avatar-upload" });
      return url;
    } catch (error) {
      console.error("Avatar upload error:", error);
      message.error({
        content: "Failed to upload avatar",
        key: "avatar-upload",
      });
      throw error;
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleAvatarChange = ({ file, fileList: newFileList }) => {
    const latest = newFileList.slice(-1);
    setAvatarFileList(latest);

    // Show instant local preview
    const latestFile = latest[0];
    if (latestFile?.originFileObj) {
      const reader = new FileReader();
      reader.onload = (e) => setAvatarPreviewUrl(e.target.result);
      reader.readAsDataURL(latestFile.originFileObj);
    }

    if (file.status === "removed") {
      setAvatarFileList([]);
      setAvatarPreviewUrl(null);
      setAvatarCloudinaryUrl(null);
    }
  };

  const handleRemoveAvatar = () => {
    setAvatarFileList([]);
    setAvatarPreviewUrl(null);
    setAvatarCloudinaryUrl(null);
  };

  const avatarUploadProps = {
    name: "file",
    multiple: false,
    fileList: avatarFileList,
    accept: "image/*",
    // Upload to Cloudinary immediately on selection — same as AddProductModal
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        await handleAvatarUpload(file);
        onSuccess("ok");
      } catch (error) {
        onError(error);
      }
    },
    beforeUpload: (file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("You can only upload image files!");
        return Upload.LIST_IGNORE;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error("Image must be smaller than 2MB!");
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    onChange: handleAvatarChange,
    onRemove: handleRemoveAvatar,
    showUploadList: false, // We render our own preview
  };

  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
  ];

  // ── Form submit ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Only start loading AFTER validation passes
      setIsSubmitting(true);

      // ── Avatar resolution (reliable, same logic as AddProductModal) ──────
      // Priority: freshly uploaded Cloudinary URL → existing from edit mode
      let avatarUrl = avatarCloudinaryUrl;

      if (!avatarUrl && mode === "edit" && employeeData?.avatar) {
        avatarUrl = employeeData.avatar;
      }

      // Transform form values to API shape then append avatar
      const submitData = transformFormDataToApi({
        ...values,
        avatar: avatarUrl || undefined, // only include key if we have a value
      });

      await onSubmit?.(submitData);

      // Reset on successful create
      if (mode === "create") {
        form.resetFields();
        setAvatarFileList([]);
        setAvatarPreviewUrl(null);
        setAvatarCloudinaryUrl(null);
        onFormValidChange?.(false);
      }
    } catch (error) {
      console.error("Error preparing form data:", error);

      if (error.errorFields) {
        // Ant Design validation error — already shown by form, do nothing extra
        return;
      }

      // API / unexpected errors are handled by the parent's onSubmit
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <Form
        form={form}
        layout="vertical"
        className="space-y-4"
        onFinish={handleSubmit}
        onValuesChange={() => setTimeout(checkFormValidity, 0)}
        onFieldsChange={() => setTimeout(checkFormValidity, 0)}
        onFinishFailed={(errorInfo) => {
          console.error("Form validation failed:", errorInfo);
          setIsSubmitting(false);
        }}
      >
        {/* Name Fields */}
        <NameFieldsRow readOnly={mode === "view"} />

        {/* Email */}
        <EmailField readOnly={mode === "view"} />

        {/* Phone and Gender */}
        <PhoneGenderRow
          genderOptions={genderOptions}
          readOnly={mode === "view"}
        />

        {/* Team and Role */}
        <TeamRoleRow
          teamOptions={rawTeamsData}
          roleOptions={rawRolesData}
          isLoadingTeams={isLoadingTeams}
          isLoadingRoles={isLoadingRoles}
          readOnly={mode === "view"}
        />

        {/* Address */}
        <TextField
          name="address"
          label="Address"
          placeholder="e.g 123 Example Street, Lagos, Nigeria"
          required
          readOnly={mode === "view"}
        />

        {mode === "view" && (
          <TextField
            value={employeeData?.roles?.map((role) => role.name).join(", ")}
            label="Role(s)"
            readOnly={true}
          />
        )}

        {/* ── Avatar Upload (inline — same pattern as AddProductModal) ── */}
        {mode !== "view" && (
          <Form.Item label="Profile Photo (Optional)">
            <div className="flex items-start gap-4">
              {/* Preview */}
              <div className="relative flex-shrink-0">
                {avatarPreviewUrl ? (
                  <>
                    <img
                      src={avatarPreviewUrl}
                      alt="avatar preview"
                      className="w-20 h-20 rounded-full object-cover border-2 border-orange-200 shadow-sm"
                    />
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={handleRemoveAvatar}
                      className="absolute -top-1 -right-1 bg-white border border-red-200 rounded-full shadow-sm"
                      title="Remove photo"
                    />
                  </>
                ) : (
                  <div className="w-20 h-20 rounded-full bg-orange-50 border-2 border-dashed border-orange-200 flex items-center justify-center">
                    <UserOutlined className="text-2xl text-orange-300" />
                  </div>
                )}
              </div>

              {/* Dragger */}
              <div className="flex-1">
                <Dragger
                  {...avatarUploadProps}
                  className="border-dashed border-gray-300 rounded-lg hover:border-orange-400 transition-colors"
                  style={{ padding: "12px" }}
                >
                  <p className="ant-upload-drag-icon mb-1">
                    <UploadOutlined className="text-2xl text-gray-400" />
                  </p>
                  <p className="text-sm text-gray-700 font-medium mb-0">
                    {isUploadingAvatar
                      ? "Uploading…"
                      : "Click or drag photo here"}
                  </p>
                  <p className="text-xs text-gray-400 mt-0">
                    JPG, PNG (Max 2MB)
                  </p>
                </Dragger>
              </div>
            </div>

            {/* Upload status indicator */}
            {isUploadingAvatar && (
              <p className="text-xs text-orange-500 mt-1 animate-pulse">
                Uploading to cloud, please wait…
              </p>
            )}
            {avatarCloudinaryUrl && !isUploadingAvatar && (
              <p className="text-xs text-green-600 mt-1">✓ Photo ready</p>
            )}
          </Form.Item>
        )}

        {/* Show avatar in view mode */}
        {mode === "view" && employeeData?.avatar && (
          <Form.Item label="Profile Photo">
            <img
              src={employeeData.avatar}
              alt="avatar"
              className="w-20 h-20 rounded-full object-cover border-2 border-orange-200 shadow-sm"
            />
          </Form.Item>
        )}

        {/* Location */}
        <LocationField
          locationOptions={rawLocationsData}
          isLoadingLocations={isLoadingLocations}
          readOnly={mode === "view"}
        />
      </Form>
    </div>
  );
};

export default EmployeeSingleUpload;
